import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export type PublicationStatus =
  | 'nova'
  | 'lida'
  | 'vinculada'
  | 'prazo_sugerido'
  | 'tarefa_criada'
  | 'arquivada'
  | 'ignorada';

export interface Publication {
  id: string;
  provider: string;
  source: string | null;
  externalId: string | null;
  sourceName: string | null;
  tribunal: string | null;
  processNumber: string | null;
  title: string;
  content: string;
  summary: string | null;
  availableAt: string | null;
  publishedAt: string | null;
  capturedAt: string;
  lawyerOab: string | null;
  lawyerName: string | null;
  partyNames: string | null;
  rawJson: string | null;
  hash: string | null;
  status: PublicationStatus;
  notifiedAt: string | null;
  notificationChannel: string | null;
  notificationStatus: string | null;
  notificationError: string | null;
  linkedCaseId: string | null;
  linkedClientId: string | null;
  matchType: string | null;
  confidence: number | null;
  confirmedByUser: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePublicationInput {
  provider?: string | null;
  source?: string | null;
  externalId?: string | null;
  sourceName?: string | null;
  tribunal?: string | null;
  processNumber?: string | null;
  title: string;
  content?: string | null;
  summary?: string | null;
  availableAt?: string | null;
  publishedAt?: string | null;
  capturedAt?: string | null;
  lawyerOab?: string | null;
  lawyerName?: string | null;
  partyNames?: string | string[] | null;
  rawJson?: string | Record<string, unknown> | null;
  hash?: string | null;
  status?: PublicationStatus | string | null;
  notifiedAt?: string | null;
  notificationChannel?: string | null;
  notificationStatus?: string | null;
  notificationError?: string | null;
  linkedCaseId?: string | null;
  linkedClientId?: string | null;
  matchType?: string | null;
  confidence?: number | string | null;
  confirmedByUser?: boolean | number | string | null;
}

export type UpdatePublicationInput = Partial<CreatePublicationInput>;

type UsePublicationsOptions = {
  autoLoad?: boolean;
};

type PublicationResponse = {
  success?: boolean;
  publications?: unknown;
  publication?: unknown;
  error?: string;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toBoolean = (value: unknown, fallback = false) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['0', 'false', 'inactive', 'inativo', 'archived', 'arquivado', 'nao', 'não'].includes(normalized)) {
    return false;
  }

  return true;
};

const toNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numeric = Number(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizePublicationStatus = (value: unknown): PublicationStatus => {
  const normalized = String(value ?? 'nova').trim().toLowerCase();
  const map: Record<string, PublicationStatus> = {
    nova: 'nova',
    lida: 'lida',
    vinculada: 'vinculada',
    'vinculada ao caso': 'vinculada',
    prazo_sugerido: 'prazo_sugerido',
    'prazo sugerido': 'prazo_sugerido',
    tarefa_criada: 'tarefa_criada',
    'tarefa criada': 'tarefa_criada',
    arquivada: 'arquivada',
    ignorada: 'ignorada',
    'em analise': 'lida',
    'em análise': 'lida',
  };

  return map[normalized] ?? 'nova';
};

const normalizePublication = (value: Record<string, unknown> | null | undefined): Publication => ({
  id: String(value?.id ?? ''),
  provider: String(value?.provider ?? 'manual').trim() || 'manual',
  source: toNullableText(value?.source),
  externalId: toNullableText(value?.externalId ?? value?.external_id),
  sourceName: toNullableText(value?.sourceName ?? value?.source_name),
  tribunal: toNullableText(value?.tribunal),
  processNumber: toNullableText(value?.processNumber ?? value?.process_number),
  title: String(value?.title ?? '').trim(),
  content: String(value?.content ?? '').trim(),
  summary: toNullableText(value?.summary),
  availableAt: toNullableText(value?.availableAt ?? value?.available_at),
  publishedAt: toNullableText(value?.publishedAt ?? value?.published_at),
  capturedAt: toNullableText(value?.capturedAt ?? value?.captured_at) ?? new Date().toISOString(),
  lawyerOab: toNullableText(value?.lawyerOab ?? value?.lawyer_oab),
  lawyerName: toNullableText(value?.lawyerName ?? value?.lawyer_name),
  partyNames: toNullableText(value?.partyNames ?? value?.party_names),
  rawJson: toNullableText(value?.rawJson ?? value?.raw_json),
  hash: toNullableText(value?.hash),
  status: normalizePublicationStatus(value?.status),
  notifiedAt: toNullableText(value?.notifiedAt ?? value?.notified_at),
  notificationChannel: toNullableText(value?.notificationChannel ?? value?.notification_channel),
  notificationStatus: toNullableText(value?.notificationStatus ?? value?.notification_status),
  notificationError: toNullableText(value?.notificationError ?? value?.notification_error),
  linkedCaseId: toNullableText(value?.linkedCaseId ?? value?.matched_case_id),
  linkedClientId: toNullableText(value?.linkedClientId ?? value?.matched_client_id),
  matchType: toNullableText(value?.matchType ?? value?.matched_match_type),
  confidence: toNumber(value?.confidence ?? value?.matched_confidence),
  confirmedByUser: toBoolean(value?.confirmedByUser ?? value?.matched_confirmed_by_user),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

const parseTimestamp = (value: string | null) => {
  if (!value) {
    return Number.NEGATIVE_INFINITY;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const sortPublications = (a: Publication, b: Publication) => {
  const aPriority = Number(a.status === 'arquivada' || a.status === 'ignorada');
  const bPriority = Number(b.status === 'arquivada' || b.status === 'ignorada');

  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }

  const aDate = parseTimestamp(a.availableAt ?? a.publishedAt ?? a.capturedAt);
  const bDate = parseTimestamp(b.availableAt ?? b.publishedAt ?? b.capturedAt);

  if (aDate !== bDate) {
    return bDate - aDate;
  }

  const aCreated = Date.parse(a.createdAt);
  const bCreated = Date.parse(b.createdAt);

  if (Number.isFinite(aCreated) && Number.isFinite(bCreated)) {
    return bCreated - aCreated;
  }

  return b.id.localeCompare(a.id);
};

const setSortedPublications = (setter: Dispatch<SetStateAction<Publication[]>>, next: Publication[]) => {
  setter([...next].sort(sortPublications));
};

export function usePublications(options: UsePublicationsOptions = {}) {
  const { autoLoad = true } = options;
  const [publications, setPublications] = useState<Publication[]>([]);
  const [isLoadingPublications, setIsLoadingPublications] = useState(true);
  const [isSavingPublication, setIsSavingPublication] = useState(false);
  const [publicationsError, setPublicationsError] = useState<string | null>(null);
  const [publicationsSuccess, setPublicationsSuccess] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadPublications = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoadingPublications(true);
    setPublicationsError(null);

    try {
      const response = await fetchWithRetry('/api/publications');
      const data = (await response.json()) as PublicationResponse;

      if (!response.ok || !data.success || !Array.isArray(data.publications)) {
        throw new Error(data.error || `GET /api/publications falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        setSortedPublications(setPublications, (data.publications as unknown[]).map((publication) => normalizePublication(publication as Record<string, unknown>)));
      }
    } catch (error) {
      console.error('Erro ao carregar publicações.', error);
      if (requestId === requestIdRef.current) {
        setPublications([]);
        setPublicationsError('Não foi possível carregar as publicações reais.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingPublications(false);
      }
    }
  }, []);

  const createPublication = useCallback(async (input: CreatePublicationInput) => {
    setIsSavingPublication(true);
    setPublicationsError(null);
    setPublicationsSuccess(null);

    try {
      const response = await fetchWithRetry('/api/publications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const data = (await response.json()) as PublicationResponse;

      if (!response.ok || !data.success || !data.publication) {
        throw new Error(data.error || `POST /api/publications falhou com status ${response.status}`);
      }

      const created = normalizePublication(data.publication as Record<string, unknown>);
      setPublications((prev) => [created, ...prev.filter((item) => item.id !== created.id)].sort(sortPublications));
      setPublicationsSuccess('Publicação cadastrada com sucesso.');
      return created;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Não foi possível cadastrar a publicação.';
      setPublicationsError(message);
      throw error;
    } finally {
      setIsSavingPublication(false);
    }
  }, []);

  const updatePublication = useCallback(async (id: string, input: UpdatePublicationInput) => {
    setIsSavingPublication(true);
    setPublicationsError(null);
    setPublicationsSuccess(null);

    try {
      const response = await fetchWithRetry(`/api/publications/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const data = (await response.json()) as PublicationResponse;

      if (!response.ok || !data.success || !data.publication) {
        throw new Error(data.error || `PUT /api/publications/${id} falhou com status ${response.status}`);
      }

      const updated = normalizePublication(data.publication as Record<string, unknown>);
      setPublications((prev) => [updated, ...prev.filter((item) => item.id !== updated.id)].sort(sortPublications));
      setPublicationsSuccess('Publicação atualizada com sucesso.');
      return updated;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Não foi possível atualizar a publicação.';
      setPublicationsError(message);
      throw error;
    } finally {
      setIsSavingPublication(false);
    }
  }, []);

  const archivePublication = useCallback(async (id: string) => {
    setIsSavingPublication(true);
    setPublicationsError(null);
    setPublicationsSuccess(null);

    try {
      const response = await fetchWithRetry(`/api/publications/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });

      const data = (await response.json()) as PublicationResponse;

      if (!response.ok || !data.success) {
        throw new Error(data.error || `DELETE /api/publications/${id} falhou com status ${response.status}`);
      }

      setPublications((prev) =>
        prev
          .map((publication) =>
            publication.id === id
              ? {
                  ...publication,
                  status: 'arquivada' as PublicationStatus,
                  updatedAt: new Date().toISOString(),
                }
              : publication
          )
          .sort(sortPublications)
      );
      setPublicationsSuccess('Publicação arquivada com sucesso.');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Não foi possível arquivar a publicação.';
      setPublicationsError(message);
      throw error;
    } finally {
      setIsSavingPublication(false);
    }
  }, []);

  const markPublicationAsRead = useCallback(
    async (id: string) => updatePublication(id, { status: 'lida' }),
    [updatePublication]
  );

  const ignorePublication = useCallback(
    async (id: string) => updatePublication(id, { status: 'ignorada' }),
    [updatePublication]
  );

  const linkPublication = useCallback(
    async (id: string, input: UpdatePublicationInput) =>
      updatePublication(id, {
        ...input,
        status: 'vinculada',
      }),
    [updatePublication]
  );

  const clearPublicationFeedback = useCallback(() => {
    setPublicationsError(null);
    setPublicationsSuccess(null);
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      setIsLoadingPublications(false);
      return;
    }

    void loadPublications();
  }, [autoLoad, loadPublications]);

  return {
    publications,
    isLoadingPublications,
    isSavingPublication,
    publicationsError,
    publicationsSuccess,
    loadPublications,
    createPublication,
    updatePublication,
    archivePublication,
    markPublicationAsRead,
    ignorePublication,
    linkPublication,
    clearPublicationFeedback,
  };
}
